import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import Notification from '../components/ui/Notification';
import PublishModal from '../components/publishing/PublishModal';
import ReportConfigModal from '../features/sessions/report/components/ReportConfigModal';
import SessionReportGenerateModal from '../features/sessions/report/components/SessionReportGenerateModal';
import DeleteSessionModal from '../features/sessions/detail/components/modals/DeleteSessionModal';
import FixSectionsModal from '../features/sessions/detail/components/modals/FixSectionsModal';
import RegenerateTranscriptModal from '../features/sessions/detail/components/modals/RegenerateTranscriptModal';
import SessionHeader from '../features/sessions/detail/components/SessionHeader';
import SessionSidebar from '../features/sessions/detail/components/SessionSidebar';
import SessionTabPanel from '../features/sessions/detail/components/SessionTabPanel';
import { useCitationJump } from '../features/sessions/detail/hooks/useCitationJump';
import { usePublishReport } from '../features/sessions/detail/hooks/usePublishReport';
import { useSessionDetail } from '../features/sessions/detail/hooks/useSessionDetail';
import { useSessionReport } from '../features/sessions/detail/hooks/useSessionReport';
import { useSessionTranscript } from '../features/sessions/detail/hooks/useSessionTranscript';
import { useTranscriptGaps } from '../features/sessions/detail/hooks/useTranscriptGaps';
import { useTranscriptGeneration } from '../features/sessions/detail/hooks/useTranscriptGeneration';
import { sessionTabs } from '../features/sessions/detail/utils/sessionTabs';
import '../features/sessions/detail/styles/session-detail.css';

const SessionUnavailable = ({ notif }) => (
  <div className="session-detail-page">
    <div className="session-detail-content">
      <div className="session-detail-empty">
        {notif ? <><strong>{notif.title}</strong><span>{notif.message}</span></> : 'Loading session...'}
      </div>
    </div>
  </div>
);

const TabBar = ({ tabs, active, onSelect }) => (
  <div className="session-main-tabs">
    {tabs.map((t) => (
      <button key={t.id} type="button" onClick={() => onSelect(t.id)} className={active === t.id ? 'active' : ''}>
        {t.label}
        {t.count !== undefined && <span>{t.count}</span>}
      </button>
    ))}
  </div>
);

export default function SessionDetailPage() {
  const { id } = useParams();
  const [notif, setNotif] = useState(null);
  const [activeTab, setActiveTab] = useState('transcript');
  const [reportVersion, setReportVersion] = useState('official');
  const [openModal, setOpenModal] = useState(null);
  const showTranscript = useCallback(() => setActiveTab('transcript'), []);
  const { highlight, jumpToCitation } = useCitationJump(showTranscript);

  const { session, setSession, reloadSession } = useSessionDetail(id, setNotif);
  const primaryLanguage = String(session?.primary_language || session?.transcript_language || 'en').toLowerCase();
  const transcript = useSessionTranscript(id, session, setSession, reloadSession);
  const report = useSessionReport(id, primaryLanguage, setNotif, reloadSession);
  const generation = useTranscriptGeneration(id, session, setNotif, reloadSession);
  const gaps = useTranscriptGaps(id, generation.language, setNotif);
  const publish = usePublishReport(id, session, report.languages, primaryLanguage, setNotif, reloadSession);

  if (!session) return <SessionUnavailable notif={notif} />;

  const transcriptReady = session.transcript_status === 'COMPLETED' || transcript.segments.length > 0;
  const canGenerateReport = transcriptReady && !report.isStarting && !report.busy;
  const tabs = sessionTabs({ segmentCount: transcript.segments.length, report, hasReport: report.exists || report.hasContent });
  const tab = tabs.some((t) => t.id === activeTab) ? activeTab : 'transcript';

  const openGenerate = () => setOpenModal('generate');
  const closeModal = () => setOpenModal(null);
  const generateWith = (closeFirst) => async (options) => {
    if (closeFirst) closeModal();
    await report.generate(options);
    closeModal();
  };

  return (
    <div className="session-detail-page">
      <SessionHeader session={session} onDelete={() => setOpenModal('delete')} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <TabBar tabs={tabs} active={tab} onSelect={setActiveTab} />
          <SessionTabPanel
            tab={tab}
            sessionId={id}
            session={session}
            transcript={transcript}
            report={report}
            highlight={highlight}
            primaryLanguage={primaryLanguage}
            canGenerateReport={canGenerateReport}
            onGenerateReport={openGenerate}
            onCitationClick={jumpToCitation}
            onReportVersionChange={setReportVersion}
            notify={setNotif}
          />
        </div>

        <SessionSidebar
          tab={tab}
          sessionId={id}
          session={session}
          transcript={transcript}
          transcriptReady={transcriptReady}
          generation={generation}
          gaps={gaps}
          report={report}
          publish={publish}
          isOfficialReport={reportVersion === 'official'}
          canGenerateReport={canGenerateReport}
          onGenerateReport={openGenerate}
          onAdvancedReport={() => setOpenModal('advanced')}
          primaryLanguage={primaryLanguage}
          reloadSession={reloadSession}
          notify={setNotif}
        />
      </div>

      <RegenerateTranscriptModal generation={generation} />
      <FixSectionsModal fixer={gaps} />
      <DeleteSessionModal session={session} isOpen={openModal === 'delete'} onClose={closeModal} notify={setNotif} />
      <SessionReportGenerateModal
        isOpen={openModal === 'generate'}
        onClose={closeModal}
        onSubmit={generateWith(true)}
        sessionTitle={session.title}
        isRegenerating={report.exists}
        initialSettings={session.report_settings}
      />
      <ReportConfigModal
        isOpen={openModal === 'advanced'}
        onClose={closeModal}
        onSubmit={generateWith(false)}
        initialSettings={session.report_settings}
      />
      <PublishModal
        isOpen={Boolean(publish.destination)}
        onClose={publish.close}
        onConfirm={publish.confirm}
        destination={publish.destination}
        availableDestinations={publish.destinations}
        onDestinationChange={publish.setDestination}
        isUpdate={Boolean(session.url)}
        isPublishing={publish.isPublishing}
        availableLanguages={report.languages}
      />

      {notif && (
        <Notification
          type={notif.type}
          title={notif.title}
          message={notif.message}
          onClose={() => setNotif(null)}
          duration={5000}
        />
      )}
    </div>
  );
}
